/**
 * Phase 3 — RFQ Negotiation drawer.
 * Timeline of negotiation rounds + "Add Round" form, plus the emergency
 * sourcing justification workflow (justify → second-person approval).
 */
import React, { useCallback, useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Zap, MessageSquareText, Plus, ShieldCheck } from 'lucide-react';
import { apiGet, apiPost } from '../../lib/api';
import { formatDate } from '../../lib/format';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from '../../components/ui/sheet';

const inp = 'w-full text-xs border border-slate-200 rounded-sm px-2 py-1.5 focus:outline-none focus:border-[#3b4872]';

export default function RFQNegotiationDrawer({ rfq, onClose, onChanged }) {
  const [log, setLog] = useState([]);
  const [detail, setDetail] = useState(rfq);
  const [form, setForm] = useState(null);
  const [emForm, setEmForm] = useState(null);
  const [busy, setBusy] = useState(false);
  const [vendorNamesById, setVendorNamesById] = useState({});

  const reload = useCallback(() => {
    apiGet(`/rfq/${rfq.id}/negotiation-log`).then((d) => setLog(d?.items || [])).catch(() => {});
    apiGet(`/rfq/${rfq.id}`).then(setDetail).catch(() => {});
  }, [rfq.id]);
  useEffect(() => { reload(); }, [reload]);

  const vendors = detail?.vendors?.length ? detail.vendors : (detail?.vendor_ids || []);
  // Legacy RFQs may only carry raw vendor_ids (no resolved `vendors` name list) —
  // resolve names for the dropdown/log so we never show a bare UUID (Procurement
  // Verification Round 2, polish #2).
  useEffect(() => {
    if (detail?.vendors?.length) return;
    const ids = (detail?.vendor_ids || []).filter(id => !vendorNamesById[id]);
    if (!ids.length) return;
    Promise.all(ids.map(id => apiGet(`/vendors/${id}`).then(v => [id, v?.name]).catch(() => [id, null])))
      .then(pairs => setVendorNamesById(prev => ({ ...prev, ...Object.fromEntries(pairs.filter(([, n]) => n)) })));
  }, [detail?.vendors, detail?.vendor_ids, vendorNamesById]);
  const vendorLabel = (v) => vendorNamesById[v] || v;
  // Item 6 (Fixes_Combined): a multi-item quote needs a revised rate PER LINE —
  // one blanket rate was being applied to every line of the quote.
  const pickedQuote = (detail?.quotes || []).find((q) => {
    const qv = q.vendor_id || q.vendor || q.vendor_name || '';
    return form?.vendor_name && qv === form.vendor_name;
  });
  const quoteLines = (pickedQuote?.line_items || []);
  const isEmergency = detail?.sourcing_route === 'emergency';
  const emApproved = !!detail?.justification_approved_by;

  const addRound = async () => {
    if (!form.vendor_name) { toast.error('Pick the vendor for this round'); return; }
    setBusy(true);
    try {
      const perLine = quoteLines
        .filter((li) => (form.line_rates || {})[li.id] !== undefined && (form.line_rates || {})[li.id] !== '')
        .map((li) => ({ line_item_id: li.id, revised_rate: Number(form.line_rates[li.id]) }));
      const revised_rates = perLine.length
        ? perLine
        : (form.revised_rate ? [{ line_item_id: 'all', revised_rate: Number(form.revised_rate) }] : []);
      if (quoteLines.length > 0 && perLine.length === 0 && form.revised_rate) {
        toast.error('This is a multi-item quote — enter the revised rate per line.');
        setBusy(false); return;
      }
      await apiPost(`/rfq/${rfq.id}/negotiation-round`, {
        vendor_name: vendorLabel(form.vendor_name),
        vendor_id: form.vendor_name,
        counter_offer_notes: form.notes || '',
        revised_rates,
      });
      toast.success('Negotiation round logged');
      setForm(null); reload(); onChanged?.();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed'); }
    finally { setBusy(false); }
  };

  const justifyEmergency = async () => {
    if (!emForm.justification?.trim()) { toast.error('Justification is required'); return; }
    setBusy(true);
    try {
      await apiPost(`/rfq/${rfq.id}/justify-emergency`, emForm);
      toast.success('Flagged as emergency purchase — needs one extra approval before award');
      setEmForm(null); reload(); onChanged?.();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed'); }
    finally { setBusy(false); }
  };

  const approveJustification = async () => {
    setBusy(true);
    try {
      await apiPost(`/rfq/${rfq.id}/approve-emergency-justification`);
      toast.success('Emergency justification approved');
      reload(); onChanged?.();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed'); }
    finally { setBusy(false); }
  };

  return (
    <Sheet open onOpenChange={(o) => !o && onClose()}>
      <SheetContent className="w-full sm:max-w-lg overflow-y-auto" data-testid="rfq-negotiation-drawer">
        <SheetHeader>
          <SheetTitle className="font-display flex items-center gap-2">
            <MessageSquareText size={16} className="text-[#3b4872]" /> Negotiation · {detail?.rfq_no || rfq.rfq_no}
          </SheetTitle>
          <SheetDescription>{detail?.item || detail?.project_name || ''}</SheetDescription>
        </SheetHeader>

        {/* Emergency section */}
        <div className="mt-4 border border-slate-200 rounded-sm p-3 bg-slate-50" data-testid="rfq-emergency-section">
          {isEmergency ? (
            <div className="space-y-1.5">
              <div className="flex items-center gap-2">
                <span className="px-1.5 py-0.5 text-[10px] bg-amber-100 text-amber-800 rounded-sm font-display font-bold uppercase">⚡ Emergency</span>
              {emApproved ? (
                <span className="px-1.5 py-0.5 text-[10px] bg-emerald-50 text-emerald-700 rounded-sm font-display font-bold uppercase" data-testid="rfq-em-approved-badge">Justification approved</span>
              ) : (
                <span className="px-1.5 py-0.5 text-[10px] bg-red-50 text-red-700 rounded-sm font-display font-bold uppercase">Awaiting justification approval</span>
              )}
            </div>
            <p className="text-xs text-slate-700">{detail?.justification}</p>
            {detail?.single_vendor_reason && <p className="text-[11px] text-slate-500">Single vendor: {detail.single_vendor_reason}</p>}
            <p className="text-[10px] text-slate-400">Flagged by {detail?.justified_by} · {formatDate(detail?.justified_at)}</p>
            {emApproved ? (
              <>
                <p className="text-[10px] text-emerald-600 flex items-center gap-1"><ShieldCheck size={11} /> Approved by {detail.justification_approved_by} · {formatDate(detail.justification_approved_at)}</p>
                {detail?.self_approved && (
                  <span className="inline-flex items-center gap-1 px-1.5 py-0.5 text-[10px] bg-amber-50 text-amber-700 border border-amber-200 rounded-sm font-display font-bold uppercase" data-testid="rfq-em-self-approved-badge">
                    {detail.self_approved_basis === 'procurement_group' ? 'Self-approved (Procurement team)' : `Self-approved (${detail.self_approved_role})`}
                  </span>
                )}
              </>
            ) : (
                <button onClick={approveJustification} disabled={busy} className="mt-1 px-2.5 py-1.5 text-xs bg-emerald-600 text-white rounded-sm font-display font-bold disabled:opacity-50" data-testid="rfq-em-approve-btn">
                  Approve Justification
                </button>
              )}
            </div>
          ) : emForm ? (
            <div className="space-y-2" data-testid="rfq-em-form">
              <div className="kpi-overline flex items-center gap-1"><Zap size={11} /> Flag as Emergency / Spot Purchase</div>
              <textarea placeholder="Justification * (e.g. plant breakdown, safety-critical)" value={emForm.justification || ''}
                onChange={(e) => setEmForm({ ...emForm, justification: e.target.value })} className={`${inp} h-16`} data-testid="rfq-em-justification" />
              <input placeholder="Single-vendor reason (if applicable)" value={emForm.single_vendor_reason || ''}
                onChange={(e) => setEmForm({ ...emForm, single_vendor_reason: e.target.value })} className={inp} />
              <div className="flex gap-2">
                <button onClick={justifyEmergency} disabled={busy} className="px-2.5 py-1.5 text-xs bg-amber-600 text-white rounded-sm font-display font-bold disabled:opacity-50" data-testid="rfq-em-save">Flag Emergency</button>
                <button onClick={() => setEmForm(null)} className="px-2.5 py-1.5 text-xs text-slate-600 hover:bg-slate-100 rounded-sm">Cancel</button>
              </div>
            </div>
          ) : (
            <button onClick={(e) => { e.currentTarget.blur(); setEmForm({}); }} className="flex items-center gap-1.5 text-xs text-amber-700 font-display font-bold" data-testid="rfq-em-flag-btn">
              <Zap size={12} /> Flag as emergency / spot purchase
            </button>
          )}
        </div>

        {/* Negotiation timeline */}
        <div className="mt-4">
          <div className="kpi-overline mb-2">Negotiation Rounds ({log.length})</div>
          {log.length === 0 && <div className="text-xs text-slate-400 py-4 text-center">No rounds logged yet</div>}
          <div className="space-y-2">
            {log.map((r) => (
              <div key={r.id} className="border-l-2 border-[#3b4872] pl-3 py-1" data-testid={`rfq-neg-round-${r.round_no}`}>
                <div className="flex items-center gap-2 text-xs">
                  <span className="font-display font-bold text-[#3b4872]">Round {r.round_no}</span>
                  <span className="text-slate-600">{vendorLabel(r.vendor_name)}</span>
                  <span className="text-slate-400 text-[10px] ml-auto">{formatDate(r.logged_at)} · {r.logged_by}</span>
                </div>
                {r.counter_offer_notes && <p className="text-xs text-slate-700 mt-0.5">{r.counter_offer_notes}</p>}
                {(r.revised_rates || []).map((rr, i) => (
                  <span key={i} className="inline-block mt-1 mr-1 px-1.5 py-0.5 text-[10px] bg-slate-100 rounded-sm tabular-nums">
                    {rr.line_item_id && rr.line_item_id !== 'all' ? `${rr.line_item_name || 'Line'}: ` : 'Revised rate: '}
                    ₹{Number(rr.revised_rate).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                  </span>
                ))}
              </div>
            ))}
          </div>

          {form ? (
            <div className="mt-3 border border-slate-200 rounded-sm p-3 bg-slate-50 space-y-2" data-testid="rfq-neg-form">
              <select value={form.vendor_name || ''} onChange={(e) => setForm({ ...form, vendor_name: e.target.value })} className={inp} data-testid="rfq-neg-vendor">
                <option value="">— Vendor —</option>
                {vendors.map((v) => <option key={v} value={v}>{vendorLabel(v)}</option>)}
              </select>
              <textarea placeholder="Counter-offer notes" value={form.notes || ''} onChange={(e) => setForm({ ...form, notes: e.target.value })} className={`${inp} h-14`} data-testid="rfq-neg-notes" />
              {quoteLines.length > 0 ? (
                <div className="border border-slate-200 rounded-sm divide-y bg-white" data-testid="rfq-neg-line-rates">
                  <div className="px-2 py-1 text-[10px] uppercase tracking-widest text-slate-500">Revised rate per line</div>
                  {quoteLines.map((li) => (
                    <div key={li.id} className="flex items-center gap-2 px-2 py-1.5">
                      <span className="flex-1 min-w-0 truncate text-[11px]">{li.material_name || li.description}
                        <span className="text-slate-400"> · {li.qty} {li.uom} · now ₹{Number(li.rate || 0).toFixed(2)}</span>
                      </span>
                      <input type="number" step="any" min="0" placeholder="New rate"
                        value={(form.line_rates || {})[li.id] ?? ''}
                        onChange={(e) => setForm({ ...form, line_rates: { ...(form.line_rates || {}), [li.id]: e.target.value } })}
                        className="w-24 text-xs border border-slate-200 rounded-sm px-2 py-1 text-right tabular-nums"
                        data-testid={`rfq-neg-line-rate-${li.id}`} />
                    </div>
                  ))}
                </div>
              ) : (
                <input type="number" step="any" placeholder="Revised rate (optional)" value={form.revised_rate || ''} onChange={(e) => setForm({ ...form, revised_rate: e.target.value })} className={inp} data-testid="rfq-neg-revised-rate" />
              )}
              <div className="flex gap-2">
                <button onClick={addRound} disabled={busy} className="px-2.5 py-1.5 text-xs bg-[#3b4872] text-white rounded-sm font-display font-bold disabled:opacity-50" data-testid="rfq-neg-save">Log Round</button>
                <button onClick={() => setForm(null)} className="px-2.5 py-1.5 text-xs text-slate-600 hover:bg-slate-100 rounded-sm">Cancel</button>
              </div>
            </div>
          ) : (
            <button onClick={(e) => { e.currentTarget.blur(); setForm({}); }} className="mt-3 flex items-center gap-1 px-2.5 py-1.5 text-xs bg-[#3b4872] text-white rounded-sm font-display font-bold" data-testid="rfq-neg-add-btn">
              <Plus size={11} /> Add Round
            </button>
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
}
